import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Job, Query, VehicleType } from "../generated/nest-graphql";
import { GET_VEHICLE_JOB_HISTORY } from "../graphql/queries/getVehicleJobHistory";
import {
  byServiceDateDesc,
  isCompletedVisit,
  jobMatchesVehicle,
  ServiceVisit,
  toServiceVisits,
} from "../components/VehicleHistory/vehicleHistoryData";

/** The resolver defaults to 100; naming it keeps the truncation notice honest. */
export const JOB_HISTORY_LIMIT = 100;

export type VehicleJobHistory = {
  vehicleJobs: Job[];
  services: ServiceVisit[];
  truncated: boolean;
  loading: boolean;
  error?: Error;
  refetch: () => Promise<unknown>;
};

export const useVehicleJobHistory = (contactId?: string | null, vehicle?: VehicleType | null): VehicleJobHistory => {
  const { data, loading, error, refetch } = useQuery<Query>(GET_VEHICLE_JOB_HISTORY, {
    variables: { filter: { contact: contactId }, limit: JOB_HISTORY_LIMIT },
    skip: !contactId,
    // Fresh on every open, as the old job history page was, and never written into the cache that the open
    // job's form and inspection read.
    fetchPolicy: "no-cache",
  });

  const allJobs = useMemo(
    () => ((data?.getJobs?.edges ?? []).filter(Boolean) as Job[]).slice().sort(byServiceDateDesc),
    [data]
  );

  const vehicleJobs = useMemo(() => allJobs.filter((job) => jobMatchesVehicle(job, vehicle)), [allJobs, vehicle]);

  return {
    vehicleJobs,
    // Only closed jobs: an open or cancelled job's services were never done.
    services: useMemo(() => toServiceVisits(vehicleJobs.filter(isCompletedVisit)), [vehicleJobs]),
    truncated: !!data?.getJobs?.pageInfo?.hasNextPage,
    loading,
    // A failed re-read of a list already on screen keeps the list rather than replacing it with an error.
    error: data ? undefined : (error as Error | undefined),
    refetch,
  };
};
